// ViewRole.jsx
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    Home as HomeIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    Timeline as TimelineIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    Link,
    Paper,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CustomButton from '../../common/button/commonButton';
import { fetchRoles } from '../../redux/role/roleAction';
import { ColoredCheckbox } from './commonactions';

const ViewRole = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { roleId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // State
  const [loading, setLoading] = useState(true);
  const [roleData, setRoleData] = useState(null);
  const [permissionsData, setPermissionsData] = useState({});
  const [expandedModule, setExpandedModule] = useState(false);

  // Redux state
  const roleState = useSelector((state) => state?.role?.fetchRoles || {});

  // Available modules
  const modules = [
    { id: 1, name: 'adminDashboard', label: 'Admin Dashboard', icon: '🏠', category: 'Core' },
    { id: 2, name: 'dashboard', label: 'Dashboard', icon: '📊', category: 'Core' },
    { id: 3, name: 'patient', label: 'Patient Management', icon: '🏥', category: 'Medical' },
    { id: 4, name: 'admissions', label: 'Admissions', icon: '📝', category: 'Medical' },
    { id: 5, name: 'doctor', label: 'Doctor Management', icon: '👨‍⚕️', category: 'Staff' },
    { id: 6, name: 'securityOfficer', label: 'Security Officer', icon: '🛡️', category: 'Security' },
    { id: 7, name: 'staff', label: 'Staff Management', icon: '👥', category: 'Staff' },
    { id: 8, name: 'block', label: 'Block Management', icon: '🏢', category: 'Infrastructure' },
    { id: 9, name: 'floor', label: 'Floor Management', icon: '🏗️', category: 'Infrastructure' },
    { id: 10, name: 'ward', label: 'Ward Management', icon: '🏨', category: 'Infrastructure' },
    { id: 11, name: 'department', label: 'Department', icon: '🏛️', category: 'Organization' },
    { id: 12, name: 'specialization', label: 'Specialization', icon: '🎯', category: 'Medical' },
    { id: 13, name: 'patientTransfer', label: 'Patient Transfer', icon: '🚛', category: 'Operations' },
    { id: 14, name: 'adminTransfer', label: 'Admin Transfer', icon: '🔄', category: 'Operations' },
    { id: 15, name: 'history', label: 'History', icon: '📚', category: 'Records' },
    { id: 16, name: 'rolePermission', label: 'Role Permission', icon: '🔐', category: 'Security' },
    { id: 17, name: 'adminProfile', label: 'Admin Profile', icon: '👤', category: 'Profile' },
    { id: 18, name: 'staffProfile', label: 'Staff Profile', icon: '👥', category: 'Profile' },
    { id: 19, name: 'doctorProfile', label: 'Doctor Profile', icon: '👨‍⚕️', category: 'Profile' },
    { id: 20, name: 'staffPatientList', label: 'Staff Patient List', icon: '📋', category: 'Records' },
    { id: 21, name: 'report', label: 'Reports', icon: '📈', category: 'Analytics' },
  ];

  const moduleNameMapping = {
    'RolePerminsions': 'rolePermission',
    'floor': 'floor',
  };

  // Fetch role data on component mount
  useEffect(() => {
    dispatch(fetchRoles(1));
  }, [dispatch]);

  // Process fetched role data
  useEffect(() => {
    if (roleState.success && roleState.Roles && roleState.Roles.data) {
      const currentRole = roleState.Roles.data.find(role => role.id.toString() === roleId);
      if (currentRole) {
        setRoleData(currentRole);
        
        // Parse and set permissions
        try {
          const permissions = JSON.parse(currentRole.permissions);
          const formattedPermissions = {};
          
          modules.forEach(module => {
            const normalizedModuleName = moduleNameMapping[module.name] || module.name;
            const modulePermissions = permissions[module.name] || permissions[normalizedModuleName] || {};
            
            formattedPermissions[module.id] = {
              add: modulePermissions.add || false,
              edit: modulePermissions.edit || false,
              view: modulePermissions.view || false,
              delete: modulePermissions.delete || false,
              allAccess: modulePermissions.allaccess || false,
              noAccess: modulePermissions.noaccess || false,
            };
          });
          
          setPermissionsData(formattedPermissions);
          setLoading(false);
        } catch (error) {
          console.error('Error parsing role permissions:', error);
          setLoading(false);
        }
      } else {
        navigate('/role-permissions');
      }
    }
  }, [roleState, roleId, navigate]);

  const handleEdit = () => {
    navigate(`/role-permissions/edit/${roleId}`);
  };

  const handleBack = () => {
    navigate('/role-permissions');
  };

  const getPermissionSummary = () => {
    let totalPermissions = 0;
    let activePermissions = 0;
    let fullAccessModules = 0;
    let noAccessModules = 0;

    Object.values(permissionsData).forEach(modulePerms => {
      totalPermissions += 4; // add, edit, view, delete
      if (modulePerms.add) activePermissions++;
      if (modulePerms.edit) activePermissions++;
      if (modulePerms.view) activePermissions++;
      if (modulePerms.delete) activePermissions++;
      if (modulePerms.allAccess) fullAccessModules++;
      if (modulePerms.noAccess) noAccessModules++;
    });

    return {
      totalPermissions,
      activePermissions,
      fullAccessModules,
      noAccessModules,
      permissionPercentage: totalPermissions > 0 ? Math.round((activePermissions / totalPermissions) * 100) : 0
    };
  };

  const getModulesByCategory = () => {
    const categories = {};
    modules.forEach(module => {
      if (!categories[module.category]) {
        categories[module.category] = [];
      }
      categories[module.category].push(module);
    });
    return categories;
  };

  const getPermissionIcon = (permissionType) => {
    const icons = {
      view: <VisibilityIcon sx={{ fontSize: 20 }} />,
      add: <AddIcon sx={{ fontSize: 20 }} />,
      edit: <EditIcon sx={{ fontSize: 20 }} />,
      delete: <DeleteIcon sx={{ fontSize: 20 }} />,
      allAccess: <LockOpenIcon sx={{ fontSize: 20 }} />,
      noAccess: <LockIcon sx={{ fontSize: 20 }} />
    };
    return icons[permissionType] || null;
  };

  const getPermissionColor = (permissionType) => {
    const colors = {
      view: '#2196F3',
      add: '#4CAF50',
      edit: '#FF9800',
      delete: '#F44336',
      allAccess: '#673AB7',
      noAccess: '#9E9E9E'
    };
    return colors[permissionType] || '#90A4AE';
  };

  const getPermissionLabel = (permissionType) => {
    const labels = {
      view: 'View',
      add: 'Add',
      edit: 'Edit',
      delete: 'Delete',
      allAccess: 'All Access',
      noAccess: 'No Access'
    };
    return labels[permissionType] || permissionType;
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedModule(isExpanded ? panel : false);
  };

  const summary = getPermissionSummary();
  const modulesByCategory = getModulesByCategory();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
      >
        <CircularProgress size={60} sx={{ color: '#13a7b9' }} />
      </Box>
    );
  }

  return (
    <Box p={isMobile ? 2 : 3} sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs 
          aria-label="breadcrumb" 
          sx={{ mb: 2 }}
          separator="›"
        >
          <Link 
            underline="hover" 
            color="inherit" 
            href="/dashboard"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={handleBack}
            sx={{ cursor: 'pointer' }}
          >
            Role Permissions
          </Link>
          <Typography color="text.primary">View Role</Typography>
        </Breadcrumbs>

        {/* Page Title and Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ color: '#13a7b9' }}
            >
              Back
            </Button>
            <Avatar sx={{ bgcolor: '#13a7b9', width: 56, height: 56 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight="bold" 
                color="#13a7b9"
              >
                {roleData?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role ID: {roleData?.id} | Serial: {roleData?.serial_no || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          <CustomButton
            text="Edit Role"
            type="submit"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          />
        </Box>
      </Paper>

      {/* Role Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #4CAF50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                    {summary.activePermissions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Permissions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #2196F3' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SecurityIcon sx={{ color: '#2196F3', fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#2196F3">
                    {summary.permissionPercentage}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Permission Coverage
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #673AB7' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LockOpenIcon sx={{ color: '#673AB7', fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#673AB7">
                    {summary.fullAccessModules}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Full Access Modules
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid #9E9E9E' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LockIcon sx={{ color: '#9E9E9E', fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#9E9E9E">
                    {summary.noAccessModules}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No Access Modules
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Role Status and Info */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#13a7b9' }}>
          Role Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" fontWeight="bold">Status:</Typography>
              <Chip
                label={roleData?.status === "1" ? "Active" : "Inactive"}
                color={roleData?.status === "1" ? "success" : "error"}
                size="small"
                icon={roleData?.status === "1" ? <CheckCircleIcon /> : <CancelIcon />}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" fontWeight="bold">Created:</Typography>
              <Typography variant="body2" color="text.secondary">
                {roleData?.created_at ? new Date(roleData.created_at).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" fontWeight="bold">Total Modules:</Typography>
              <Chip label={modules.length} color="primary" size="small" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Module Permissions by Category */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#13a7b9' }}>
          Module Permissions by Category
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {Object.entries(modulesByCategory).map(([category, categoryModules]) => {
          const categoryActivePerms = categoryModules.reduce((acc, module) => {
            const modulePerms = permissionsData[module.id];
            if (modulePerms) {
              acc += (modulePerms.add ? 1 : 0) + (modulePerms.edit ? 1 : 0) + 
                     (modulePerms.view ? 1 : 0) + (modulePerms.delete ? 1 : 0);
            }
            return acc;
          }, 0);
          
          const categoryTotalPerms = categoryModules.length * 4;
          const categoryPercentage = categoryTotalPerms > 0 ? Math.round((categoryActivePerms / categoryTotalPerms) * 100) : 0;
          
          return (
            <Accordion 
              key={category}
              expanded={expandedModule === category}
              onChange={handleAccordionChange(category)}
              sx={{ mb: 1, borderRadius: 1, '&:before': { display: 'none' } }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  backgroundColor: '#f8f9fa',
                  borderRadius: 1,
                  '&.Mui-expanded': { 
                    backgroundColor: '#e3f2fd' 
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TimelineIcon sx={{ color: '#13a7b9' }} />
                    <Typography variant="h6" fontWeight="bold">
                      {category}
                    </Typography>
                    <Chip 
                      label={`${categoryModules.length} modules`} 
                      size="small" 
                      variant="outlined" 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {categoryActivePerms}/{categoryTotalPerms} permissions
                    </Typography>
                    <Chip 
                      label={`${categoryPercentage}%`} 
                      size="small" 
                      color={categoryPercentage > 50 ? "success" : categoryPercentage > 25 ? "warning" : "error"}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              
              <AccordionDetails>
                <Grid container spacing={2}>
                  {categoryModules.map(module => {
                    const modulePerms = permissionsData[module.id] || {};
                    
                    return (
                      <Grid item xs={12} key={module.id}>
                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                          <CardContent sx={{ pb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h6">{module.icon}</Typography>
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {module.label}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {module.name}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              {modulePerms.allAccess && (
                                <Chip 
                                  label="Full Access" 
                                  color="success" 
                                  size="small"
                                  icon={<LockOpenIcon />}
                                />
                              )}
                              {modulePerms.noAccess && (
                                <Chip 
                                  label="No Access" 
                                  color="error" 
                                  size="small"
                                  icon={<LockIcon />}
                                />
                              )}
                            </Box>
                            
                            <Divider sx={{ mb: 2 }} />
                            
                            <Grid container spacing={2}>
                              {['view', 'add', 'edit', 'delete'].map(permission => (
                                <Grid item xs={6} sm={3} key={permission}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: modulePerms[permission] ? 
                                      `${getPermissionColor(permission)}20` : '#f5f5f5',
                                    border: `1px solid ${modulePerms[permission] ? 
                                      getPermissionColor(permission) : '#e0e0e0'}`
                                  }}>
                                    <ColoredCheckbox 
                                      checked={modulePerms[permission] || false}
                                      color={getPermissionColor(permission)}
                                      disabled={true}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {getPermissionIcon(permission)}
                                      <Typography 
                                        variant="body2" 
                                        fontWeight={modulePerms[permission] ? "bold" : "normal"}
                                        color={modulePerms[permission] ? getPermissionColor(permission) : "text.secondary"}
                                      >
                                        {getPermissionLabel(permission)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Paper>
      
      {/* Footer Actions */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ minWidth: 120 }}
        >
          Back to List
        </Button>
        <CustomButton
          text="Edit Role"
          type="submit"
          startIcon={<EditIcon />}
          onClick={handleEdit}
        />
      </Box>
    </Box>
  );
};

export default ViewRole;